import React, { useEffect, useRef, useState } from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import { find, get } from 'lodash';
import { Editor } from '@tinymce/tinymce-react';

import { DownloadLink } from 'common-components';
import { useUploadFileMutation } from 'app/apis/upload.api';
import { DEFAULT_CONFIG_NAME, useConfig } from 'app/config.ctx';

const IconWrapper = ({ onClick, children }) => {
  return <Box
    display={'flex'}
    justifyContent={'center'}
    alignItems={'center'}
    onClick={onClick}
    style={{
      cursor: 'pointer',
      color: "white",
      width: 37,
      height: 37,
      backgroundColor: '#04629A',
      borderRadius: '50%'
    }}>
    {children}</Box>
}

const MainText = ({ name, lang }) => {
  const { t } = useTranslation();
  const [uploadFile] = useUploadFileMutation();
  const uploadInputRef = useRef(null);
  const bookletUploadInputRef = useRef(null);
  const editorRef = useRef(null);
  const [currValue, setCurrValue] = useState({});
  const [bodyTextEn, setBodyTextEn] = useState(null);
  const [bodyTextFr, setBodyTextFr] = useState(null);
  const [bookletFileData, setBookletFileData] = useState({ uri: "", format: "" });
  const [fileData, setFileData] = useState({ uri: "", format: "" });
  const [open, setOpen] = useState(false);
  const { getConfigValue } = useConfig();

  useEffect(() => {
    const langRefEn = find(get(currValue, 'ref'), r => r.lang === 'en');
    const langRefFr = find(get(currValue, 'ref'), r => r.lang === 'fr');

    if (lang === "en" && langRefEn) {
      setFileData({ format: langRefEn.format, uri: langRefEn.uri });
    } else if (langRefFr) {
      setFileData({ format: langRefFr.format, uri: langRefFr.uri });
    }

    const bookletLangRefEn = find(get(currValue, 'orig'), r => r.lang === 'en');
    const bookletLangRefFr = find(get(currValue, 'orig'), r => r.lang === 'fr');

    if (lang === "en" && bookletLangRefEn) {
      setBookletFileData({ format: bookletLangRefEn.format, uri: bookletLangRefEn.uri });
    } else if (bookletLangRefFr) {
      setBookletFileData({ format: bookletLangRefFr.format, uri: bookletLangRefFr.uri });
    }

  }, [currValue, lang]);

  useEffect(() => {
    const langRefEn = find(get(currValue, 'ref'), r => r.lang === 'en');
    const langRefFr = find(get(currValue, 'ref'), r => r.lang === 'fr');

    if (langRefEn?.uri) {
      fetch(`${getConfigValue(DEFAULT_CONFIG_NAME.BASE_URL)}${langRefEn.uri}`)
        .then(resp => resp.text())
        .then(bodyText => {
          setBodyTextEn(bodyText);
        });
    } else {
      setBodyTextEn(null);
    }

    if (langRefFr?.uri) {
      fetch(`${getConfigValue(DEFAULT_CONFIG_NAME.BASE_URL)}${langRefFr.uri}`)
        .then(resp => resp.text())
        .then(bodyText => {
          setBodyTextFr(bodyText);
        });
    } else {
      setBodyTextFr(null);
    }
  }, [fileData]);

  const onSetFile = (f, onChange) => {
    let tmp = currValue.ref;
    if (!tmp) {
      tmp = [];
      tmp[lang === "en" ? 0 : 1] = { lang }
    }

    let index = tmp[0].lang === lang ? 0 : 1;
    tmp[index] = { format: f.format, lang, uri: f.uri };
    onChange({ ...currValue, ref: tmp });
  }

  const setBookletFile = (f, onChange) => {
    let tmp = currValue.orig;
    if (!tmp) {
      tmp = [];
      tmp[lang === "en" ? 0 : 1] = { lang }
    }

    let index = tmp[0].lang === lang ? 0 : 1;
    tmp[index] = { format: f.format, lang, uri: f.uri };
    onChange({ ...currValue, orig: tmp });
  }

  const onUpload = async (file, set, onChange, uploadType = 'mainText') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('uploadType', uploadType);

    const uploadResult = await uploadFile(formData).unwrap();
    set({
      name: file.name,
      format: uploadResult.format,
      uri: uploadResult.temporaryLocation,
    }, onChange);

  }

  const checkIfDocExists = () => {
    const langRef = find(get(currValue, 'ref'), r => r.lang === lang);
    return langRef?.uri
  }

  const checkIfBookletExists = () => {
    const langRef = find(get(currValue, 'orig'), r => r.lang === lang);
    return langRef?.uri
  }

  const openEdit = () => {
    setOpen(true);
  }

  const onEditorSave = (data, set, onChange) => {
    let file = new File([data], "main-text-" + lang + ".html", { type: "text/html" });
    onUpload(file, set, onChange)
      .then(() => setOpen(false));
  }

  return (
    <Controller
      name={name}
      render={({ field }) => {
        if (field.value)
          setCurrValue(field.value)
        return (
          <>
            <Grid container>
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={6} p={2}>
                    <Box sx={{ fontWeight: 'medium' }} mb={1}>
                      Public View HTML or PDF
                    </Box>
                    {!checkIfDocExists() && t('instrumentEditPublicViewText')}
                    <Stack direction={'row'} spacing={2} style={{ borderBottom: "1px solid gray" }} pb={1}>
                      {fileData.format !== "pdf" && (
                        <IconWrapper onClick={openEdit}>
                          <EditOutlinedIcon/>
                        </IconWrapper>
                      )}
                      {checkIfDocExists() && (
                        <IconWrapper
                          onClick={() => {
                            let index = currValue.ref[0].lang === lang ? 0 : 1;
                            let tmp = currValue.ref;
                            tmp[index].uri = "";
                            tmp[index].format = "";
                            field.onChange({ ...currValue, ref: tmp });
                          }}
                        >
                          <DeleteOutlineOutlinedIcon/>
                        </IconWrapper>
                      )}
                      {checkIfDocExists() && (
                        <DownloadLink uri={fileData.uri}>
                          <IconWrapper><FileDownloadOutlinedIcon/></IconWrapper>
                        </DownloadLink>
                      )}
                      <IconWrapper onClick={() => uploadInputRef.current.click()}>
                        <FileUploadOutlinedIcon/>
                      </IconWrapper>
                      <Box
                        display={'flex'}
                        justifyContent={'center'}
                        alignItems={'center'}
                      >
                        HTML, PDF
                      </Box>
                    </Stack>
                  </Grid>
                  <Grid item xs={6} p={2}>
                    <Box sx={{ fontWeight: 'medium' }} mb={1}>
                      Booklet Word or PDF
                    </Box>
                    {!checkIfBookletExists() && t('instrumentEditBookletText')}
                    <Stack direction={'row'} spacing={2} style={{ borderBottom: "1px solid gray" }} pb={1}>
                      {checkIfBookletExists() && (
                        <>
                          <IconWrapper
                            onClick={() => {
                              let index = currValue.orig[0].lang === lang ? 0 : 1;
                              let tmp = currValue.orig;
                              tmp[index].uri = "";
                              tmp[index].format = "";
                              field.onChange({ ...currValue, orig: tmp });
                            }}
                          >
                            <DeleteOutlineOutlinedIcon/>
                          </IconWrapper>
                          <DownloadLink uri={bookletFileData?.uri}>
                            <IconWrapper>
                              <FileDownloadOutlinedIcon/>
                            </IconWrapper>
                          </DownloadLink>
                        </>
                      )}
                      <IconWrapper onClick={() => bookletUploadInputRef.current.click()}>
                        <FileUploadOutlinedIcon/>
                      </IconWrapper>
                      <Box
                        display={'flex'}
                        justifyContent={'center'}
                        alignItems={'center'}
                      >
                        PDF, DOCX
                      </Box>
                    </Stack>
                  </Grid>
                  <Grid item xs={12}>
                    {fileData.format === "pdf" ? (
                      <object
                        data={`${fileData.uri}#view=fitH,100&amp;scrollbar=1&amp;toolbar=0&amp;navpanes=1`}
                        type="application/pdf" height="900px" width="100%"
                      >
                        <p>
                          Your browser does not suppport viewing PDF files, or not configured properly to display
                          embedded pdf files.
                        </p>
                      </object>
                    ) : (
                      <div
                        dangerouslySetInnerHTML={{ __html: lang === 'fr' ? DOMPurify.sanitize(bodyTextFr) : DOMPurify.sanitize(bodyTextEn) }}
                      />
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <input
              type='file'
              onChange={(e) => onUpload(e.target.files[0], onSetFile, field.onChange)}
              style={{ display: 'none' }}
              accept='.pdf,.html,.htm,.xhtml'
              ref={uploadInputRef}
            />
            <input
              type='file'
              onChange={(e) => onUpload(e.target.files[0], setBookletFile, field.onChange)}
              style={{ display: 'none' }}
              accept='.docx,.pdf'
              ref={bookletUploadInputRef}
            />
            <Dialog
              disableEnforceFocus={true}
              PaperProps={{ sx: { minHeight: 710 } }}
              fullWidth={true} maxWidth={'xl'}
              onClose={() => setOpen(false)} open={open}
            >
              <Box sx={{ backgroundColor: 'white' }} p={3}>
                <Box>
                  <h3>Edit Public Text - {lang}</h3>
                </Box>
                <Box>
                  <Editor
                    tinymceScriptSrc={process.env.PUBLIC_URL + '/tinymce/tinymce.min.js'}
                    onInit={(evt, editor) => editorRef.current = editor}
                    initialValue={lang === 'fr' ? bodyTextFr : bodyTextEn}
                    init={{
                      height: 550,
                      menubar: true,
                      promotion: false,
                      branding: false,
                      plugins: 'lists link image code',
                      toolbar: 'undo redo | formatselect | ' +
                        'bold italic backcolor | alignleft aligncenter ' +
                        'alignright alignjustify | bullist numlist outdent indent | ' +
                        'removeformat | link image code',
                      content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                      convert_urls: false
                    }}
                  />
                </Box>
                <Stack direction={'row'} justifyContent={'end'} spacing={1} pt={1}>
                  <Button
                    variant='contained'
                    onClick={() => {
                      onEditorSave(editorRef.current.getContent(), onSetFile, field.onChange);
                    }}
                  >
                    {t('save')}
                  </Button>
                  <Button
                    variant='contained'
                    onClick={() => setOpen(false)}
                  >
                    {t('close')}
                  </Button>
                </Stack>
              </Box>
            </Dialog>
          </>
        )
      }}
    />
  )
}

export default MainText;
